import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { LoadPointsSummary } from '@features/points-summary/store/actions/points-summary.actions';
import { PollOrder } from '@features/polling/store/polling.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { SentryService } from '@rd-lib/sentry/sentry.service';
import { PointsTransferApiService } from '@rd/core/services/points-transfer-api/points-transfer-api.service';
import { TENANT_NAME } from '@rd/core/tokens/rd-lib.token';
import { HttpError, PointsTransferMetadata } from '@rd/types';
import { of } from 'rxjs';
import { catchError, concatMap, map, switchMap } from 'rxjs/operators';
import * as actions from '../actions/checkout.actions';

@Injectable()
export class CheckoutEffects {
  constructor(
    @Inject(TENANT_NAME) private tenantName: string,
    private actions$: Actions,
    private pointsTransferApiService: PointsTransferApiService,
    private router: Router,
    private sentryService: SentryService,
  ) {}

  submitPointsTransfer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.CheckoutActionTypes.SUBMIT_POINTS_TRANSFER),
      switchMap((action: actions.SubmitPointsTransfer) => {
        const { loyaltyProgramId } = action.payload;
        const { membershipId } = action.payload.formData;
        const transferAmount = action.payload.formData.sourceAmount;
        const promocode = action.payload.promocode;

        return this.pointsTransferApiService
          .postPointsTransfer({
            promoCode: promocode,
            pointsTransfer: {
              loyaltyProgramId,
              membershipId,
              transferAmount,
            },
          })
          .pipe(
            map(
              (pointsTransfer) =>
                new actions.SubmitPointsTransferSuccess(pointsTransfer),
            ),
            catchError((error: HttpError) =>
              of(new actions.SubmitPointsTransferFail(error)),
            ),
          );
      }),
    ),
  );

  submitPointsTransferSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.CheckoutActionTypes.SUBMIT_POINTS_TRANSFER_SUCCESS),
      concatMap((action: actions.SubmitPointsTransferSuccess) => {
        const { loyaltyProgramId, loyaltyCurrencyShortName } = action.payload
          .metadata as PointsTransferMetadata;

        const orderId = action.payload.orderId;
        const finalActions = [
          new actions.ClearCheckoutFormData({ loyaltyProgramId }),
          new LoadPointsSummary(),
        ];

        if (!orderId) {
          this.sentryService.manualError(
            'No orderId in PointsTransfer response',
            {
              pointsTransactionId: action.payload.id,
            },
          );

          return finalActions;
        }

        this.tenantName === 'bnz' && loyaltyCurrencyShortName === 'Flybuys'
          ? this.router.navigate([`/transfer/success/flybuys/${orderId}`])
          : this.router.navigate([`/transfer/success/${orderId}`]);

        return [PollOrder({ orderId }), ...finalActions];
      }),
    ),
  );
}
